<!-- src/components/Communications/ActiveCallNavigationModal.vue -->
<!--
  Mounted globally in App.vue. Listens for `active-call-nav-prompt` events
  emitted by the router's beforeEach guard when the user attempts to navigate
  while an active call is in progress. Resolves the awaited choice through
  the payload's `resolve` callback so the router can proceed accordingly.

  Choices:
    - cancel     → router.next(false), stay where we are
    - newTab     → open destination in a new window (with ?noVoice=1 so the new
                   tab will skip Voice Device init), router.next(false)
    - continue   → router.next(true), navigate in this tab. Audio survives
                   in-tab navigation because VoiceCallService is module-level,
                   but the rich call UI on a customer page may unmount.
-->
<template>
  <div v-if="visible" class="fixed inset-0 z-[12000] flex items-center justify-center bg-black/50">
    <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
      <div class="mb-4 flex items-start gap-3">
        <div class="shrink-0 rounded-full bg-amber-100 p-2 text-amber-600">
          <Phone class="h-5 w-5" />
        </div>
        <div class="flex-1">
          <h2 class="mb-1 text-lg font-semibold text-gray-900">You're on an active call</h2>
          <p class="text-sm text-gray-600">
            Navigating to <span class="font-medium text-gray-800">{{ destinationLabel }}</span>
            in this tab can hide the call controls. Audio will keep playing here, but you may lose
            the rich call view. Open the destination in a new tab to keep your call view intact.
          </p>
        </div>
      </div>
      <div class="mt-5 flex flex-col gap-2">
        <button
          @click="choose('newTab')"
          class="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          <ExternalLink class="h-4 w-4" />
          Open in a new tab
        </button>
        <button
          @click="choose('continue')"
          class="rounded-md bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-800 transition hover:bg-gray-200"
        >
          Continue here (call audio stays active)
        </button>
        <button
          @click="choose('cancel')"
          class="px-4 py-2 text-sm text-gray-500 transition hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Phone, ExternalLink } from 'lucide-vue-next'
import { eventBus } from '@/utils/eventBus'

const visible = ref(false)
const destinationLabel = ref('')
let resolveFn = null

function open(payload) {
  destinationLabel.value = payload?.destination || 'a different page'
  resolveFn = payload?.resolve || null
  visible.value = true
}

function choose(choice) {
  visible.value = false
  const fn = resolveFn
  resolveFn = null
  if (fn) fn(choice)
}

function onKeydown(e) {
  if (!visible.value) return
  if (e.key === 'Escape') choose('cancel')
}

onMounted(() => {
  eventBus.on('active-call-nav-prompt', open)
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  eventBus.off?.('active-call-nav-prompt', open)
  window.removeEventListener('keydown', onKeydown)
  // If the modal unmounts mid-prompt (e.g. logout), don't leave the router hanging.
  if (resolveFn) {
    const fn = resolveFn
    resolveFn = null
    fn('cancel')
  }
})
</script>
